import { Directive, HostListener } from '@angular/core';

@Directive({
  selector: '[dateInputGuard]',
  standalone: true,
})
export class DateInputGuardDirective {
  /**
   * Set of control/navigation keys that must NOT be blocked.
   * These keys do not insert characters but are essential for:
   * - text editing
   * - cursor navigation
   * - accessibility (Tab navigation)
   * - closing the datepicker
   */
  private readonly allowedControlKeys = new Set([
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'Home',
    'End',
  ]);

  /**
   * Handles keyboard input.
   * Allows:
   * - control/navigation keys
   * - Ctrl/Cmd shortcuts (copy, paste, select all, etc.)
   * - digits and the '/' character
   * Blocks:
   * - letters
   * - special characters (except '/')
   */
  @HostListener('keydown', ['$event'])
  public onKeyDown(e: KeyboardEvent): void {
    // Allow navigation and system shortcut keys
    if (this.allowedControlKeys.has(e.key) || e.ctrlKey || e.metaKey) {
      return;
    }

    // Allow only digits and '/'
    if (!/^[0-9/]$/.test(e.key)) {
      e.preventDefault();
    }
  }

  /**
   * Handles paste events.
   * Allows pasting only text that contains digits and '/'.
   * Any other pasted content is blocked.
   */
  @HostListener('paste', ['$event'])
  public onPaste(e: ClipboardEvent): void {
    const pasted = e.clipboardData?.getData('text') ?? '';

    // Block paste if invalid characters are detected
    if (pasted && !/^[0-9/]+$/.test(pasted)) {
      e.preventDefault();
    }
  }
}
